import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { store } from './store';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { initializeAuth } from './store/slices/authSlice';
import Login from './pages/Login';
import MainLayout from './components/MainLayout';
import DashboardOverview from './pages/DashboardOverview';
// Import modular pages
import ContentCreatePage from './modules/content/pages/ContentCreatePage';
import ContentEditPage from './modules/content/pages/ContentEditPage';
import ContentListPage from './modules/content/pages/ContentListPage'; // New
import ConciergeEditPage from './modules/concierge/pages/ConciergeEditPage';
import TourManagementPage from './pages/TourManagementPage';
import TourFormPage from './modules/tour/pages/TourFormPage';
import TourViewPage from './modules/tour/pages/TourViewPage';
import IotDashboardPage from './modules/iot/pages/IotDashboardPage';
import WaypointPage from './modules/waypoint/pages/WaypointPage';
import ConciergeWayfindingPage from './pages/ConciergeWayfindingPage';

// Import main pages
import SettingsPage from './pages/SettingsPage';
import ReportsPage from './pages/ReportsPage';
import RobotManagementPage from './pages/RobotManagementPage';
import ChangelogPage from './pages/ChangelogPage';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Create QueryClient for @tanstack/react-query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// App content component that handles auth initialization
const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { token, isAuthenticated, user } = useAppSelector(state => state.auth);

  useEffect(() => {
    const appName = process.env.REACT_APP_APP_NAME || 'Robot Dashboard';
    const envLabel = process.env.REACT_APP_ENV_LABEL || '';
    const title = envLabel ? `${appName} [${envLabel}]` : appName;
    document.title = title;
  }, []);

  useEffect(() => {
    // Initialize auth if we have a token but no user data
    if (token && (!isAuthenticated || !user)) {
      dispatch(initializeAuth());
    }
  }, [dispatch, token, isAuthenticated, user]);

  // Add periodic token validation
  useEffect(() => {
    const validateToken = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken && isAuthenticated) {
        try {
          // Make a simple API call to validate token
          const response = await fetch('/api/auth/profile/', {
            headers: {
              Authorization: `Bearer ${storedToken}`,
            },
          });

          if (!response.ok) {
            // Token is invalid, clear auth state
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('selectedMuseum');
            window.dispatchEvent(new CustomEvent('authExpired'));
          }
        } catch (error) {
          // Network error or token invalid, clear auth state
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('selectedMuseum');
          window.dispatchEvent(new CustomEvent('authExpired'));
        }
      }
    };

    // Validate token immediately on mount
    validateToken();

    // Validate token every 5 minutes
    const interval = setInterval(validateToken, 5 * 60 * 1000);

    // Also validate on page visibility change (when user comes back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        validateToken();
      }
    };

    // Listen for localStorage changes (when token is manually modified)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' && e.newValue !== e.oldValue) {
        // Token was changed, validate it
        setTimeout(validateToken, 100); // Small delay to ensure localStorage is updated
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAuthenticated]);

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <DashboardOverview />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/museums"
            element={
              <ProtectedRoute>
                <Navigate to="/settings?tab=museums" replace />
              </ProtectedRoute>
            }
          />
          {/* Tour Routes */}
          <Route
            path="/tours"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TourManagementPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tours/create"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TourFormPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tours/:id"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TourViewPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tours/:id/edit"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <TourFormPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          {/* Content Routes */}
          <Route
            path="/content"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ContentListPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/content/create"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ContentCreatePage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/content/:waypoint_name/:version/edit"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ContentEditPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/content/:waypoint_name/:version"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ContentEditPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/content/management"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ContentListPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          {/* Concierge Routes */}
          <Route
            path="/content/concierge/:section/edit"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ConciergeEditPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/iot"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <IotDashboardPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/waypoints"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <WaypointPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/concierge-wayfinding"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ConciergeWayfindingPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <MainLayout>
                  <SettingsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ReportsPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/robots"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <RobotManagementPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/changelog"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <MainLayout>
                  <ChangelogPage />
                </MainLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </Router>
  );
};

function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AppContent />
        </ThemeProvider>
      </QueryClientProvider>
    </Provider>
  );
}

export default App;
